import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Brain, Target } from "lucide-react";
import BasicAdvancedWrapper, {
  BasicAdvancedWrapperProps,
} from "./components/BasicAdvancedWrapper";
import { MultiStepConfig } from "./components/MultiStepGradeModal";
import { CreateModalProps } from "./index";
import {
  LearningStylesStep,
  DifficultyDistributionStep,
  SelectionRequirementsStep,
  LearningOutcomesFocusStep,
} from "./choice-boards/steps";
import { ChoiceBoardCreateRequestDto } from "@packages/api/madlenAPI.schemas";
import ChoiceBoardSpecificFields from "./choice-boards/ChoiceBoardSpecificFields";

interface ChoiceBoardAdvancedData extends ChoiceBoardCreateRequestDto {
  preferredLearningStyles?: string[];
  difficultyDistribution?: string;
  selectionRequirements?: string;
  learningOutcomesFocus?: string[];
  customContext?: string;
}

interface ChoiceBoardCreateModalProps
  extends Omit<CreateModalProps, "onSubmit" | "themeColor"> {
  onSubmit: (input: any) => Promise<void>;
  themeColor?: string;
}

export const ChoiceBoardCreateModal: React.FC<ChoiceBoardCreateModalProps> = ({
  onSubmit,
  initialInput,
  ...props
}) => {
  const { t } = useTranslation();

  // State for basic choice board fields
  const [basicContext, setBasicContext] = useState(
    (initialInput as any).context || "",
  );

  // Define the initial data structure for advanced mode
  const initialAdvancedData: ChoiceBoardAdvancedData = {
    // Basic CreateModal data
    courseName: initialInput.courseName as any,
    gradeLevel: initialInput.gradeLevel,
    prompt: initialInput.prompt,
    environment: initialInput.environment,
    isPublic: initialInput.isPublic,
    generationType: initialInput.generationType as any,
    teacherDocumentIds: initialInput.teacherDocumentIds || [],
    learningOutcomeIds: initialInput.learningOutcomeIds || [],
    contentLanguage: initialInput.outputLanguage as any,

    // Advanced fields
    preferredLearningStyles: [],
    difficultyDistribution: "",
    selectionRequirements: "",
    learningOutcomesFocus: [],
    customContext: basicContext,
  };

  // Define the advanced steps configuration
  const advancedSteps: MultiStepConfig<ChoiceBoardAdvancedData>[] = [
    {
      id: "learning-styles",
      title: t("choiceBoards.steps.learningStyles.title"),
      description: t("choiceBoards.steps.learningStyles.description"),
      icon: <Brain size={16} />,
      component: LearningStylesStep,
      validation: (data) => {
        return !!(
          data.preferredLearningStyles &&
          data.preferredLearningStyles.length > 0
        );
      },
    },
    {
      id: "difficulty-distribution",
      title: t("choiceBoards.steps.difficultyDistribution.title"),
      description: t("choiceBoards.steps.difficultyDistribution.description"),
      icon: <Target size={16} />,
      component: DifficultyDistributionStep,
      validation: (data) => {
        return !!(
          data.difficultyDistribution && data.difficultyDistribution.trim()
        );
      },
    },
    {
      id: "selection-requirements",
      title: t("choiceBoards.steps.selectionRequirements.title"),
      description: t("choiceBoards.steps.selectionRequirements.description"),
      icon: <Target size={16} />,
      component: SelectionRequirementsStep,
      validation: (data) => {
        return !!(
          data.selectionRequirements && data.selectionRequirements.trim()
        );
      },
    },
    {
      id: "learning-outcomes-focus",
      title: t("choiceBoards.steps.learningOutcomesFocus.title"),
      description: t("choiceBoards.steps.learningOutcomesFocus.description"),
      icon: <Brain size={16} />,
      component: LearningOutcomesFocusStep,
      validation: (data) => {
        return !!(
          data.learningOutcomesFocus && data.learningOutcomesFocus.length > 0
        );
      },
    },
  ];

  // Handle basic mode submission
  const handleBasicSubmit = async (data: any) => {
    let submissionData = { ...data };

    // Combine context with prompt if context is provided
    if (basicContext.trim()) {
      submissionData.prompt = `${basicContext.trim()}\n\n${data.prompt}`;
    }

    await onSubmit(submissionData);
  };

  // Handle advanced mode submission
  const handleAdvancedSubmit = async (data: ChoiceBoardAdvancedData) => {
    // Construct enriched prompt based on advanced settings
    let enrichedPrompt = data.prompt || "";

    // Add context if provided
    if (basicContext?.trim()) {
      enrichedPrompt = `${basicContext.trim()}\n\n${enrichedPrompt}`;
    }

    // Add learning styles preferences
    if (
      data.preferredLearningStyles &&
      data.preferredLearningStyles.length > 0
    ) {
      enrichedPrompt += `\n\nPreferred Learning Styles: ${data.preferredLearningStyles.join(", ")}`;
    }

    // Add difficulty distribution
    if (data.difficultyDistribution?.trim()) {
      enrichedPrompt += `\nDifficulty Distribution: ${data.difficultyDistribution}`;
    }

    // Add selection requirements
    if (data.selectionRequirements?.trim()) {
      enrichedPrompt += `\nSelection Requirements: ${data.selectionRequirements}`;
    }

    // Add learning outcomes focus
    if (data.learningOutcomesFocus && data.learningOutcomesFocus.length > 0) {
      enrichedPrompt += `\nLearning Outcomes Focus: ${data.learningOutcomesFocus.join(", ")}`;
    }

    // Add instruction for structured output
    enrichedPrompt += `\n\nPlease provide choice board with activities that include choice_board_instructions, difficulty_level, learning_outcomes, skills_developed, and learning_style for each activity.`;

    // Clean up the advanced-specific fields before submission
    const { customContext, ...cleanedData } = data;
    cleanedData.prompt = enrichedPrompt;

    await onSubmit(cleanedData);
  };

  // Handle context change from basic mode
  const handleContextChange = (context: string) => {
    setBasicContext(context);
  };

  // Prepare the wrapper props
  const wrapperProps: BasicAdvancedWrapperProps<ChoiceBoardAdvancedData> = {
    createModalProps: {
      ...props,
      initialInput,
      themeColor: "green",
      onSubmit: handleBasicSubmit,
      backPath: "/content/choice-boards",
      submitButtonText: t("common.generate"),
      after: (
        <ChoiceBoardSpecificFields
          context={basicContext}
          onChange={handleContextChange}
          themeColor="green"
        />
      ),
    },
    multiStepProps: {
      steps: advancedSteps,
      initialData: initialAdvancedData,
      title: t("quickActions.title"),
      themeColor: "green",
      backPath: "/content/choice-boards",
      isLoading: props.isLoading,
      error: props.error,
      loadingTitle: props.loadingTitle,
      errorTitle: props.errorTitle,
      errorDescription: props.errorDescription,
      onFinalSubmit: handleAdvancedSubmit,
      basicModeSubmitText: t("common.generate"),
      advancedModeSubmitText: t("choiceBoards.createAdvanced"),
    },
    themeColor: "green",
    title: t("quickActions.choiceboards"),
    backPath: "/content/choice-boards",
    enableAdvancedMode: true,
    defaultMode: "basic",
    showModeToggle: true,
    onBasicSubmit: handleBasicSubmit,
    onAdvancedSubmit: handleAdvancedSubmit,
  };

  return <BasicAdvancedWrapper {...wrapperProps} />;
};

export default ChoiceBoardCreateModal;
